import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, API } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !token) return;

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === "paid") {
          setStatus("success");
          toast.success("Pagamento confirmado! Seu passaporte esta pronto.");
        } else if (response.data.status === "expired") {
          setStatus("error");
          toast.error("Sessao de pagamento expirada.");
        } else if (attempts < 5) {
          setAttempts(prev => prev + 1);
          setTimeout(() => pollPaymentStatus(), 2000);
        } else {
          setStatus("success"); // Assume success after max attempts
          toast.info("Verifique seu painel para confirmar o passaporte.");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < 5) {
          setAttempts(prev => prev + 1);
          setTimeout(() => pollPaymentStatus(), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, API, attempts]);

  return (
    <div className="min-h-screen bg-navy-dark flex flex-col">
      <Seo noindex title="Compra Concluída" />
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
        <Card className="bg-navy border-gold/20 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            {status === "checking" && (
              <>
                <Loader2 className="w-16 h-16 text-gold mx-auto mb-4 animate-spin" />
                <h2 className="font-heading text-2xl text-white mb-2">{t("checkoutPage.checking")}</h2>
                <p className="text-white/60 font-body">{t("checkoutPage.checkingDesc")}</p>
              </>
            )}

            {status === "success" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                >
                  <CheckCircle className="w-20 h-20 text-emerald-400 mx-auto mb-4" />
                </motion.div>
                <h2 className="font-heading text-2xl text-white mb-2">{t("checkoutPage.confirmedTitle")}</h2>
                <p className="text-white/60 font-body mb-6">
                  {t("checkoutPage.confirmedDesc")}
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full"
                    data-testid="go-to-dashboard-btn"
                  >
                    Ver Meu Passaporte
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full border-white/20 text-white hover:bg-white/10 rounded-full"
                  >
                    Voltar ao Inicio
                  </Button>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="font-heading text-2xl text-white mb-2">{t("checkoutPage.errorTitle")}</h2>
                <p className="text-white/60 font-body mb-6">
                  {t("checkoutPage.errorDesc")}
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full"
                >
                  {t("checkoutPage.tryAgain")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
      </div>
      <SiteFooter />
    </div>
  );
}
